import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card, Table, Badge, Row, Col, Form, InputGroup, Button, Collapse, Dropdown, Alert } from "react-bootstrap";
import { getTransactions } from '../../services/admin';
import PageTitle from '../../Component/Comman/PageTitle';

const Transactions = () => {
    const navigate = useNavigate();
    const [transactions, setTransactions] = useState([]);
    const [summary, setSummary] = useState({});
    const [totals, setTotals] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [pagination, setPagination] = useState({
        page: 1,
        pages: 1,
        total: 0,
        limit: 10
    });
    const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
    const [filters, setFilters] = useState({
        type: "",
        status: "",
        searchTerm: "",
        searchFields: "",
        dateRange: {
            start: "",
            end: "",
        },
        amountRange: {
            min: "",
            max: "",
        },
    });

    const fetchTransactions = async (page = 1) => {
        try {
            setLoading(true);
            setError(null);
            
            const params = {
                page,
                limit: pagination.limit
            };

            // Add search parameters if they exist
            if (filters.searchTerm || filters.dateRange.start || filters.dateRange.end || filters.amountRange.min || filters.amountRange.max) {
                params.searchTerm = filters.searchTerm;
                if (filters.searchFields) {
                    params.searchFields = filters.searchFields;
                }
                if (filters.dateRange.start && filters.dateRange.end) {
                    params.dateRange = filters.dateRange;
                }
                if (filters.amountRange.min || filters.amountRange.max) {
                    params.amountRange = filters.amountRange;
                }
            }

            // Add type and status filters
            if (filters.type) {
                params.type = filters.type;
            }
            if (filters.status) {
                params.status = filters.status;
            }

            const response = await getTransactions(params);
            
            if (!response.success && !response.data) {
                throw new Error('No data received from API');
            }

            // Ensure transactions is an array before mapping
            const transactionsArray = Array.isArray(response.data?.transactions) 
                ? response.data.transactions 
                : (Array.isArray(response.data) ? response.data : []);

            setTransactions(transactionsArray);
            setSummary(response.data?.summary || {});
            setTotals(response.data?.totals || {});
            setPagination(response.data?.pagination || { page: 1, pages: 1, total: 0, limit: pagination.limit });
            setError(null);
        } catch (error) {
            console.error('Error fetching transactions:', error);
            setError(error.message || 'Failed to fetch transactions');
            setTransactions([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTransactions(pagination.page);
    }, [pagination.page]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        if (name.includes('.')) {
            const [parent, child] = name.split('.');
            setFilters(prev => ({
                ...prev,
                [parent]: {
                    ...prev[parent],
                    [child]: value
                }
            }));
        } else {
            setFilters(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleSearch = (e) => {
        e.preventDefault();
        setPagination(prev => ({ ...prev, page: 1 }));
        fetchTransactions(1);
    };

    const resetFilters = () => {
        setFilters({
            type: "",
            status: "",
            searchTerm: "",
            searchFields: "",
            dateRange: {
                start: "",
                end: "",
            },
            amountRange: {
                min: "",
                max: "",
            },
        });
        setPagination(prev => ({ ...prev, page: 1 }));
        fetchTransactions(1);
    };

    const handlePageChange = (page) => {
        setPagination(prev => ({ ...prev, page }));
    };

    const getStatusBadge = (status) => {
        const badges = {
            completed: 'success',
            pending: 'warning',
            failed: 'danger'
        };
        return badges[status] || 'secondary';
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatAmount = (amount) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2
        }).format(amount || 0);
    };

    if (loading && pagination.page === 1) {
        return (
            <div className="container-xxl">
                <PageTitle pagetitle="Transactions" sidebutton={true} />
                <div className="text-center p-4">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="container-xxl">
            <PageTitle pagetitle="Transactions" sidebutton={true} />
            
            <div className="row">
                {/* Summary Cards */}
                <div className="col-xl-3 col-sm-6 mb-3">
                    <Card className="border-0 shadow-sm">
                        <Card.Body>
                            <div className="d-flex align-items-center justify-content-between">
                                <div>
                                    <h4 className="mb-0">{totals.totalTransactions || 0}</h4>
                                    <h5 className="mb-0 text-muted">Total Transactions</h5>
                                </div>
                                <div className="icon-box bg-primary-light rounded-circle" style={{ width: '60px', height: '60px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <i className="fa fa-exchange-alt text-primary fa-2x"></i>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </div>
                <div className="col-xl-3 col-sm-6 mb-3">
                    <Card className="border-0 shadow-sm">
                        <Card.Body>
                            <div className="d-flex align-items-center justify-content-between">
                                <div>
                                    <h4 className="mb-0">{formatAmount(totals.totalAmount || 0)}</h4>
                                    <h5 className="mb-0 text-muted">Total Amount</h5>
                                </div>
                                <div className="icon-box bg-success-light rounded-circle" style={{ width: '60px', height: '60px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <i className="fa fa-dollar-sign text-success fa-2x"></i>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </div>
                <div className="col-xl-3 col-sm-6 mb-3">
                    <Card className="border-0 shadow-sm">
                        <Card.Body>
                            <div className="d-flex align-items-center justify-content-between">
                                <div>
                                    <h4 className="mb-0">{formatAmount(totals.pendingAmount || 0)}</h4>
                                    <h5 className="mb-0 text-muted">Pending Amount</h5>
                                </div>
                                <div className="icon-box bg-warning-light rounded-circle" style={{ width: '60px', height: '60px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <i className="fa fa-clock text-warning fa-2x"></i>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </div>
                <div className="col-xl-3 col-sm-6 mb-3">
                    <Card className="border-0 shadow-sm">
                        <Card.Body>
                            <div className="d-flex align-items-center justify-content-between">
                                <div>
                                    <h4 className="mb-0">{formatAmount(totals.completedAmount || 0)}</h4>
                                    <h5 className="mb-0 text-muted">Completed Amount</h5>
                                </div>
                                <div className="icon-box bg-success-light rounded-circle" style={{ width: '60px', height: '60px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <i className="fa fa-check text-success fa-2x"></i>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </div>

                {/* Transaction Type Summary */}
                {(summary.deposit || summary.withdrawal || summary.bonus) && (
                    <div className="col-12 mb-3">
                        <Card className="border-0 shadow-sm">
                            <Card.Header className="bg-white border-bottom">
                                <Card.Title className="mb-0">Transaction Summary</Card.Title>
                            </Card.Header>
                            <Card.Body>
                                <Row>
                                    <Col md={4}>
                                        <h5>Deposits</h5>
                                        <ul className="list-group">
                                            <li className="list-group-item d-flex justify-content-between align-items-center">
                                                Pending
                                                <span>
                                                    {summary.deposit?.pending?.count || 0} ({formatAmount(summary.deposit?.pending?.amount || 0)})
                                                </span>
                                            </li>
                                            <li className="list-group-item d-flex justify-content-between align-items-center">
                                                Completed
                                                <span>
                                                    {summary.deposit?.completed?.count || 0} ({formatAmount(summary.deposit?.completed?.amount || 0)})
                                                </span>
                                            </li>
                                            <li className="list-group-item d-flex justify-content-between align-items-center">
                                                Failed
                                                <span>
                                                    {summary.deposit?.failed?.count || 0} ({formatAmount(summary.deposit?.failed?.amount || 0)})
                                                </span>
                                            </li>
                                        </ul>
                                    </Col>
                                    <Col md={4}>
                                        <h5>Withdrawals</h5>
                                        <ul className="list-group">
                                            <li className="list-group-item d-flex justify-content-between align-items-center">
                                                Pending
                                                <span>
                                                    {summary.withdrawal?.pending?.count || 0} ({formatAmount(summary.withdrawal?.pending?.amount || 0)})
                                                </span>
                                            </li>
                                            <li className="list-group-item d-flex justify-content-between align-items-center">
                                                Completed
                                                <span>
                                                    {summary.withdrawal?.completed?.count || 0} ({formatAmount(summary.withdrawal?.completed?.amount || 0)})
                                                </span>
                                            </li>
                                            <li className="list-group-item d-flex justify-content-between align-items-center">
                                                Failed
                                                <span>
                                                    {summary.withdrawal?.failed?.count || 0} ({formatAmount(summary.withdrawal?.failed?.amount || 0)})
                                                </span>
                                            </li>
                                        </ul>
                                    </Col>
                                    <Col md={4}>
                                        <h5>Bonus</h5>
                                        <ul className="list-group">
                                            <li className="list-group-item d-flex justify-content-between align-items-center">
                                                Completed
                                                <span>
                                                    {summary.bonus?.completed?.count || 0} ({formatAmount(summary.bonus?.completed?.amount || 0)})
                                                </span>
                                            </li>
                                        </ul>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    </div>
                )}

                {/* Transactions Table */}
                <div className="col-12">
                    <Card className="border-0 shadow-sm">
                        <Card.Header className="bg-white border-bottom">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <Card.Title className="mb-0">All Transactions</Card.Title>
                                {/* <Link to="/admin/transactions/create" className="btn btn-primary">
                                    <i className="fa fa-plus me-2"></i>Create Transaction
                                </Link> */}
                            </div>
                            <Form onSubmit={handleSearch}>
                                <div className="d-flex gap-3 align-items-center mb-3">
                                    <Form.Group className="mb-0" style={{ minWidth: '300px' }}>
                                        <InputGroup>
                                            <Form.Control
                                                type="text"
                                                placeholder="Search transactions..."
                                                name="searchTerm"
                                                value={filters.searchTerm}
                                                onChange={handleFilterChange}
                                            />
                                            <InputGroup.Text>
                                                <i className="fa fa-search"></i>
                                            </InputGroup.Text>
                                        </InputGroup>
                                    </Form.Group>
                                    <Button 
                                        variant="link" 
                                        onClick={() => setShowAdvancedSearch(!showAdvancedSearch)}
                                        className="text-primary"
                                    >
                                        {showAdvancedSearch ? 'Hide Advanced Search' : 'Advanced Search'}
                                    </Button>
                                </div>

                                <Collapse in={showAdvancedSearch}>
                                    <div>
                                        <Row className="mb-3">
                                            <Col md={3}>
                                                <Form.Group>
                                                    <Form.Label>Search In</Form.Label>
                                                    <Form.Select
                                                        name="searchFields"
                                                        value={filters.searchFields}
                                                        onChange={handleFilterChange}
                                                    >
                                                        <option value="">All Fields</option>
                                                        <option value="reference">Reference</option>
                                                        <option value="user.email">User Email</option>
                                                        <option value="user.name">User Name</option>
                                                        <option value="transactionId">Transaction ID</option>
                                                    </Form.Select>
                                                </Form.Group>
                                            </Col>
                                            <Col md={3}>
                                                <Form.Group>
                                                    <Form.Label>Type</Form.Label>
                                                    <Form.Select
                                                        name="type"
                                                        value={filters.type}
                                                        onChange={handleFilterChange}
                                                    >
                                                        <option value="">All Types</option>
                                                        <option value="deposit">Deposit</option>
                                                        <option value="withdrawal">Withdrawal</option>
                                                        <option value="bonus">Bonus</option>
                                                    </Form.Select>
                                                </Form.Group>
                                            </Col>
                                            <Col md={3}>
                                                <Form.Group>
                                                    <Form.Label>Status</Form.Label>
                                                    <Form.Select
                                                        name="status"
                                                        value={filters.status}
                                                        onChange={handleFilterChange}
                                                    >
                                                        <option value="">All Status</option>
                                                        <option value="pending">Pending</option>
                                                        <option value="completed">Completed</option>
                                                        <option value="failed">Failed</option>
                                                    </Form.Select>
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                        <Row className="mb-3">
                                            <Col md={3}>
                                                <Form.Group>
                                                    <Form.Label>Start Date</Form.Label>
                                                    <Form.Control
                                                        type="date"
                                                        name="dateRange.start"
                                                        value={filters.dateRange.start}
                                                        onChange={handleFilterChange}
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={3}>
                                                <Form.Group>
                                                    <Form.Label>End Date</Form.Label>
                                                    <Form.Control
                                                        type="date"
                                                        name="dateRange.end"
                                                        value={filters.dateRange.end}
                                                        onChange={handleFilterChange}
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={3}>
                                                <Form.Group>
                                                    <Form.Label>Min Amount</Form.Label>
                                                    <Form.Control
                                                        type="number"
                                                        name="amountRange.min"
                                                        value={filters.amountRange.min}
                                                        onChange={handleFilterChange}
                                                        placeholder="Min amount"
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={3}>
                                                <Form.Group>
                                                    <Form.Label>Max Amount</Form.Label>
                                                    <Form.Control
                                                        type="number"
                                                        name="amountRange.max"
                                                        value={filters.amountRange.max}
                                                        onChange={handleFilterChange}
                                                        placeholder="Max amount"
                                                    />
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                        <div className="d-flex gap-2">
                                            <Button type="submit" variant="primary">
                                                <i className="fa fa-search me-2"></i>Search
                                            </Button>
                                            <Button type="button" variant="danger" onClick={resetFilters}>
                                                <i className="fa fa-refresh me-2"></i>Reset
                                            </Button>
                                        </div>
                                    </div>
                                </Collapse>
                            </Form>
                        </Card.Header>
                        <Card.Body>
                            {error && (
                                <Alert variant="danger" className="mb-3">
                                    <i className="fa fa-exclamation-circle me-2"></i>
                                    {error}
                                </Alert>
                            )}

                            {loading ? (
                                <div className="text-center py-4">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <Table responsive className="table-hover">
                                        <thead className="table-light">
                                            <tr>
                                                <th>Date</th>
                                                <th>Reference</th>
                                                <th>User</th>
                                                <th>Type</th>
                                                <th>Amount</th>
                                                <th>Status</th>
                                                <th>Payment Method</th>
                                                <th>Transaction ID</th>
                                                <th>Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {transactions.length > 0 ? (
                                                transactions.map((transaction, index) => transaction && (
                                                    <tr key={transaction?._id || index}>
                                                        <td>{formatDate(transaction?.createdAt) || 'N/A'}</td>
                                                        <td>
                                                            <Link to={`/admin/transaction/${transaction?._id}`} className="text-primary">
                                                                {transaction?.reference || 'N/A'}
                                                            </Link>
                                                        </td>
                                                        <td>
                                                            {transaction?.userId ? (
                                                                <Link to={`/admin/user/${transaction.userId._id}`}>
                                                                    {transaction.userId.name || 'N/A'}
                                                                </Link>
                                                            ) : (
                                                                transaction?.user?.name || 'N/A'
                                                            )}
                                                        </td>
                                                        <td>
                                                            <Badge bg={transaction?.type === 'deposit' ? 'success' : transaction?.type === 'withdrawal' ? 'warning' : 'info'}>
                                                                {transaction?.type || 'N/A'}
                                                            </Badge>
                                                        </td>
                                                        <td>{formatAmount(transaction?.amount) || 'N/A'}</td>
                                                        <td>
                                                            <Badge bg={getStatusBadge(transaction?.status)}>
                                                                {transaction?.status || 'N/A'}
                                                            </Badge>
                                                        </td>
                                                        <td>
                                                            {transaction?.paymentMethod ? (
                                                                <>
                                                                    {transaction.paymentMethod.bankName || transaction.paymentMethod.name}
                                                                    <br />
                                                                    <small className="text-muted">{transaction.paymentMethod.accountNumber}</small>
                                                                </>
                                                            ) : '-'}
                                                        </td>
                                                        <td>{transaction?.transactionId || 'N/A'}</td>
                                                        <td>
                                                            <Dropdown>
                                                                <Dropdown.Toggle variant="primary" size="sm" className="light sharp">
                                                                    <i className="fa fa-cog"></i>
                                                                </Dropdown.Toggle>
                                                                <Dropdown.Menu>
                                                                    <Dropdown.Item onClick={() => navigate(`/admin/transaction/${transaction?._id}`)}>
                                                                        <i className="fa fa-eye me-2"></i>View Details
                                                                    </Dropdown.Item>
                                                                </Dropdown.Menu>
                                                            </Dropdown>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="9" className="text-center py-5">
                                                        <div className="text-muted">
                                                            <i className="fa fa-inbox fa-3x mb-3 d-block"></i>
                                                            <p className="mb-0">No transactions found</p>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </Table>
                                </div>
                            )}

                            {/* Pagination */}
                            {pagination && pagination.pages > 1 && (
                                <div className="d-flex justify-content-between align-items-center mt-4">
                                    <div>
                                        Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} entries
                                    </div>
                                    <div>
                                        <nav>
                                            <ul className="pagination pagination-gutter pagination-primary no-bg mb-0">
                                                <li className={`page-item page-indicator ${pagination.page === 1 ? 'disabled' : ''}`}>
                                                    <a className="page-link" href="#" onClick={(e) => {
                                                        e.preventDefault();
                                                        if (pagination.page > 1) handlePageChange(1);
                                                    }}>
                                                        <i className="fa fa-angle-double-left"></i>
                                                    </a>
                                                </li>
                                                <li className={`page-item page-indicator ${pagination.page === 1 ? 'disabled' : ''}`}>
                                                    <a className="page-link" href="#" onClick={(e) => {
                                                        e.preventDefault();
                                                        if (pagination.page > 1) handlePageChange(pagination.page - 1);
                                                    }}>
                                                        <i className="fa fa-angle-left"></i>
                                                    </a>
                                                </li>
                                                
                                                {[...Array(pagination.pages)].map((_, index) => {
                                                    const pageNumber = index + 1;
                                                    if (
                                                        pageNumber === 1 ||
                                                        pageNumber === pagination.pages ||
                                                        (pageNumber >= pagination.page - 2 && pageNumber <= pagination.page + 2)
                                                    ) {
                                                        return (
                                                            <li key={pageNumber} className={`page-item ${pageNumber === pagination.page ? 'active' : ''}`}>
                                                                <a className="page-link" href="#" onClick={(e) => {
                                                                    e.preventDefault();
                                                                    handlePageChange(pageNumber);
                                                                }}>
                                                                    {pageNumber}
                                                                </a>
                                                            </li>
                                                        );
                                                    } else if (
                                                        pageNumber === pagination.page - 3 ||
                                                        pageNumber === pagination.page + 3
                                                    ) {
                                                        return <li key={pageNumber} className="page-item"><span className="page-link">...</span></li>;
                                                    }
                                                    return null;
                                                })}

                                                <li className={`page-item page-indicator ${pagination.page === pagination.pages ? 'disabled' : ''}`}>
                                                    <a className="page-link" href="#" onClick={(e) => {
                                                        e.preventDefault();
                                                        if (pagination.page < pagination.pages) handlePageChange(pagination.page + 1);
                                                    }}>
                                                        <i className="fa fa-angle-right"></i>
                                                    </a>
                                                </li>
                                                <li className={`page-item page-indicator ${pagination.page === pagination.pages ? 'disabled' : ''}`}>
                                                    <a className="page-link" href="#" onClick={(e) => {
                                                        e.preventDefault();
                                                        if (pagination.page < pagination.pages) handlePageChange(pagination.pages);
                                                    }}>
                                                        <i className="fa fa-angle-double-right"></i>
                                                    </a>
                                                </li>
                                            </ul>
                                        </nav>
                                    </div>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default Transactions;
